import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, MessageSquare } from 'lucide-react';

export default function HouseLogsTab({ house, houseId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    entry_type: 'GENERAL',
    notes: ''
  });

  useEffect(() => {
    fetchLogs();
  }, [houseId]);

  const fetchLogs = async () => {
    try {
      const response = await api.get(`/houses/${houseId}/logs`);
      setLogs(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/houses/${houseId}/logs`, formData);
      toast.success('Log entry created successfully');
      setShowDialog(false);
      setFormData({ title: '', entry_type: 'GENERAL', notes: '' });
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create log');
    }
  };

  const getTypeBadge = (type) => {
    const variants = {
      INFO: 'default',
      INCIDENT: 'destructive',
      VISITOR: 'secondary',
      APPOINTMENT: 'default',
      MAINTENANCE: 'warning',
      GENERAL: 'secondary'
    };
    return <Badge variant={variants[type] || 'secondary'}>{type}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Entry
        </Button>
      </div>

      <div className="space-y-4">
        {logs.map((log) => (
          <Card key={log.id}>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{log.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {log.created_by_name} • {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                  {getTypeBadge(log.entry_type)}
                </div>
                {log.notes && <p className="text-sm">{log.notes}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {logs.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No log entries yet</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add Log Entry</DialogTitle>
              <DialogDescription>Create a new communication log entry</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="entry_type">Type *</Label>
                <Select
                  value={formData.entry_type}
                  onValueChange={(value) => setFormData({ ...formData, entry_type: value })}
                >
                  <SelectTrigger id="entry_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GENERAL">General</SelectItem>
                    <SelectItem value="INFO">Info</SelectItem>
                    <SelectItem value="INCIDENT">Incident</SelectItem>
                    <SelectItem value="VISITOR">Visitor</SelectItem>
                    <SelectItem value="APPOINTMENT">Appointment</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit">Create Entry</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
